import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import Input from '../../../components/Input';
import { analytic, getPageTitle, getPageUrl, googleLibrary, isValid } from '../../../utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { useAuthRequest } from '../../../services/apis/auth/useAuthRequest';
import {
    getInviteSignupEmailStepSubtitle,
    getInviteSignupPageSubtitle,
    getInviteSignupPageTitle,
    getInviteSignupSubtitle,
    getInviteSignupTitle,
    IInviteAuthContext,
    isInviteLandingResponse,
    isInvitePendingRegistration,
    parseStoredInviteValue,
    SSO_INVITE_CODE_SESSION_KEY,
    clearInvitePendingVerification,
    getInvitePendingVerification,
    setInvitePendingVerification,
} from '../../../interfaces/models/invite';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { styles } from '../../../styles/themes/style';
import SvgFragment from '../../../fragments/SvgFragment';
import {
    AuthPageContainer,
    AuthInnerContainer,
    AuthColumn,
    FullLogo,
    HeaderSection,
    AuthCard,
    AuthMethodButton,
    OrDivider,
    DividerLine,
    OrText,
    AuthTitle,
    AuthSubtitle,
    AuthLink,
    AuthVerificationCard,
    AuthVerificationCardInner,
    EmailClientButtons,
    EmailClientButton,
    DividerLineMuted,
    AuthResendLink,
    AuthCardInlineFooter,
    AuthFormFields,
    AuthField,
    AuthInputWrap,
    AuthPasswordInputWrap,
    AuthPasswordToggle,
    AuthPasswordFieldWrap,
    PasswordVisibilityIcon,
} from '../authFormCommon';
import AuthLegalFooter from '../components/AuthLegalFooter';
import AuthSignUpAgreementText from '../components/AuthSignUpAgreementText';
import AuthSignUpLoginPrompt from '../components/AuthSignUpLoginPrompt';
import InviteSignupMethodStep from '../components/InviteSignupMethodStep';
import OpenOutlookIcon from '../../../assets/icons/OpenOutlookIcon';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { saveLastLoginUser } from '../lastLoginUserStorage';
import { skipExtensionForSurveyOnboarding } from '../../../pages/onboarding/onboardingExtensionSkipUtils';

const SignUpSection = React.forwardRef((props: any, ref: any) => {
    const [values, setValues] = React.useState<any>({});
    const [valid, setValid] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [invite, setInvite] = React.useState<IInviteAuthContext>();
    const [searchParams] = useSearchParams();
    const { setAuthDataHandler, createAndLoginToWorkspaceHandler, saveOnboardingData } = useAuthHandlers()
    const { preRegisterEmail, resendVerificationEmail: resendVerificationEmailRequest, signUpGoogle, invitePublic, workOSGetAuthorizationUrl, checkEmail } = useAuthRequest();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const { showSuccessToast, showErrorToast } = useAppHandlers();
    const [emailSent, setEmailSent] = useState(false);
    const [ssoEmail, setSsoEmail] = React.useState('');
    const [ssoValid, setSsoValid] = React.useState(false);
    const [emailCheckError, setEmailCheckError] = React.useState('');
    const [emailCheckedFor, setEmailCheckedFor] = React.useState('');
    const [emailCheckLoading, setEmailCheckLoading] = React.useState(false);

    useEffect(() => {
        document.title = invite ? 'LeadDelta — Workspace invite' : 'LeadDelta Signup';
    }, [invite]);

    const googleCallback = React.useCallback((...args: any[]) => {
        if (typeof googleLibrary.callback === 'function') googleLibrary.callback(...args);
    }, []);

    const initGoogle = React.useCallback(() => {
        // Google
        if ((window as any).google) {
            // new
            (window as any).google.accounts.id.initialize({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                callback: googleCallback,
                use_fedcm_for_prompt: true
            });

            // oauth2 init token
            googleLibrary.oauth2 = (window as any).google.accounts.oauth2.initTokenClient({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                scope: 'profile email'
            });
        }
    }, []);

    const googlePrompt = React.useCallback(() => {
        if ((window as any).google) {
            googleLibrary.callback = SignupWithGoogle;

            (window as any).google.accounts.id.prompt();
        }
    }, []);

    const cleanUp = React.useCallback(() => {
        window.history.pushState({}, '', window.location.pathname);
    }, []);

    const resetEmailCheckState = React.useCallback(() => {
        setEmailCheckError('');
        setEmailCheckedFor('');
    }, []);

    const ensureEmailChecked = React.useCallback(async () => {
        const email = values.email?.trim();
        if (!isValid.email(email)) return false;
        if (emailCheckedFor === email) return !emailCheckError;

        setEmailCheckLoading(true);
        try {
            const response = await checkEmail(email);
            const exists = Boolean(response?.data?.exists);
            const nextError = exists
                ? 'The email address already exists. Please log in or register with another email address.'
                : '';
            setEmailCheckError(nextError);
            setEmailCheckedFor(email);
            return !nextError;
        } finally {
            setEmailCheckLoading(false);
        }
    }, [values.email, emailCheckedFor, emailCheckError, checkEmail]);

    const SignupWithEmail = React.useCallback(async (event?: React.FormEvent) => {
        if (event) event.preventDefault();
        setLoading(true);
    
        try {
            if (valid) {
                const canProceed = await ensureEmailChecked();
                if (!canProceed) return;
                const response = await preRegisterEmail(
                    {
                        ...values,
                        ...(!values.firstName && { firstName: values.email }),
                        ...(!values.lastName && { lastName: values.email }),
                        ...(invite?.inviteCode && { inviteCode: invite.inviteCode })
                    },
                    {
                        params: {
                            fp_uid: Cookies.get('_fprom_ref'),
                            fp_tid: Cookies.get('_fprom_tid')
                        }
                    }
                );
    
                if (response?.success) {
                    const activeInviteCode =
                        invite?.inviteCode ?? searchParams.get('invite') ?? undefined;
                    const registeredEmail = values.email?.trim();
                    if (activeInviteCode && registeredEmail) {
                        setInvitePendingVerification({
                            inviteCode: activeInviteCode,
                            email: registeredEmail,
                        });
                    }
                    showSuccessToast({ message: `Verification email sent! Please check your inbox.` });
                    setEmailSent(true);
                    setStep(2);
                    cleanUp();
                } else {
                    showErrorToast({ message: response?.message || 'Something went wrong.' });
                }
            }
        } catch (error) {
            console.error('Sign up with email error:', error);
            showErrorToast({ message: 'Unexpected error occurred.' });
        }
    
        setLoading(false);
    }, [values, invite, valid, ensureEmailChecked]);

    const resendVerificationEmail = React.useCallback(async () => {
        if (!values.email) return;
        setLoading(true);
        try {
            const response = await resendVerificationEmailRequest({ email: values.email });

            if (response?.success) {
                showSuccessToast({ message: 'Verification email sent! Please check your inbox.' });
            } else {
                showErrorToast({ message: response?.message || 'Unable to resend verification email.' });
            }
        } catch (error) {
            console.error('Resend verification email error:', error);
            showErrorToast({ message: 'Unexpected error occurred.' });
        } finally {
            setLoading(false);
        }
    }, [values.email, resendVerificationEmailRequest, showSuccessToast, showErrorToast]);

    const SignupWithGoogle = React.useCallback(async (...args: any[]) => {
        setLoading(true);
        console.log('On sign up with Google', ...args);

        const googleResponse = args[0];

        const valid_ = googleResponse.access_token || googleResponse.credential;

        const inviteValueObj = localStorage.getItem('inviteValue')

        try {
            if (valid_) {
                const body: any = {};

                const storedInvite = parseStoredInviteValue(inviteValueObj);
                if (storedInvite?.inviteCode) {
                    body['inviteCode'] = storedInvite.inviteCode;
                    localStorage.removeItem('inviteValue');
                }

                if (googleResponse.access_token) body['access_token'] = googleResponse.access_token;

                if (googleResponse.credential) body['credential'] = googleResponse.credential;

                const response = await signUpGoogle(
                    body,
                    {
                        params: {
                            fp_uid: Cookies.get('_fprom_ref'),
                            fp_tid: Cookies.get('_fprom_tid')
                        }
                    }
                );

                if (response?.user) {
                    saveLastLoginUser(response.user, 'google');
                    const newAccessToken = `Bearer ${response.token}`;
                    const newRefreshToken = response.refreshToken;
                    if (!response?.workspace) {
                        // create workspace automatically
                        await createAndLoginToWorkspaceHandler(
                            {
                                name: response?.user?.email,
                                isPersonal: true,
                                invites: []
                            },
                            { Authorization: newAccessToken, "refresh-token": newRefreshToken }
                        );
                    }

                    setAuthDataHandler({ accessToken: newAccessToken, refreshToken: newRefreshToken });

                    await skipExtensionForSurveyOnboarding(saveOnboardingData);

                    // analytic 
                    analytic({
                        event: 'free_trial_signup',
                        pageUrl: getPageUrl(),
                        pageTitle: getPageTitle(),
                        method: 'google'
                    });

                    //showSuccessToast({ message: `You signed up successfully!` });

                    // clean up 
                    cleanUp();

                    // Auto open the extension 
                    // if all the checks are good 
                    //navigate(AppRoutes.PROCESSING)
                }
                else {
                    //showErrorToast({ message: response?.message });
                }
            }
        }
        catch (error) {
            console.log('Sign up with Google', error);
        }

        setLoading(false);
    }, [invite, valid]);

    const init = React.useCallback(async () => {
        // Init Google 
        initGoogle();

        const inviteCode = searchParams.get('invite');

        if (inviteCode) {
            const storedInvite = parseStoredInviteValue(localStorage.getItem('inviteValue'));
            const landing = storedInvite?.landing ?? await invitePublic(inviteCode);

            if (isInviteLandingResponse(landing) && isInvitePendingRegistration(landing)) {
                const inviteContext = { inviteCode, landing };
                setInvite(inviteContext);
                localStorage.setItem('inviteValue', JSON.stringify(inviteContext));

                const pendingVerification = getInvitePendingVerification();
                const restoredEmail =
                    pendingVerification?.inviteCode === inviteCode
                        ? pendingVerification.email
                        : landing.invite?.email?.trim();

                if (restoredEmail) {
                    setValues((previous: any) => ({
                        ...previous,
                        email: restoredEmail,
                        firstName: restoredEmail,
                        lastName: restoredEmail,
                    }));
                    setSsoEmail(restoredEmail);
                    setSsoValid(!!isValid.email(restoredEmail) || restoredEmail.includes('.'));
                }

                if (pendingVerification?.inviteCode === inviteCode && pendingVerification.email) {
                    setEmailSent(true);
                    setStep(2);
                } else {
                    setStep(1);
                }
            }
        }

        googlePrompt();
    }, []);

    React.useEffect(() => {
        init();
    }, []);

    const getGoogleAccessToken = () => {
        return new Promise((resolve, reject) => {
            if (googleLibrary.oauth2) {
                googleLibrary.oauth2.callback = (response: any) => resolve(response);

                googleLibrary.oauth2.error_callback = (response: any) => resolve(response);

                googleLibrary.oauth2.requestAccessToken();
            }
        });
    }

    const onGoogleSignUpClick = React.useCallback(async () => {
        const response = await getGoogleAccessToken();

        // Use SignupWithGoogle method with response as an argument
        SignupWithGoogle(response);
    }, []);

    const updateValid = React.useCallback((valuesNew: any) => {
        const validNew = (
            isValid.email(valuesNew.email) &&
            valuesNew.password?.length > 7);

        setValid(validNew ?? false);
    }, []);

    const updateValue = React.useCallback((property: string, valueNew: any) => {
        setValues((previous: any) => {
            const valuesNew = {
                ...previous,
                [property]: valueNew
            };
            if (property === 'email') resetEmailCheckState();

            // Valid 
            updateValid(valuesNew);

            return valuesNew;
        });
    }, [resetEmailCheckState]);

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && valid) {
            event.preventDefault();
            SignupWithEmail();
        }
    };

    const updateSsoEmail = React.useCallback((value: string) => {
        setSsoEmail(value);
        setSsoValid(!!isValid.email(value) || value.includes('.'));
    }, []);

    const SignupWithSSO = React.useCallback(async (event?: React.FormEvent) => {
        if (event) event.preventDefault();
        setLoading(true);
        try {
            if (ssoValid) {
                const response = await workOSGetAuthorizationUrl({ 
                    email: ssoEmail 
                });

                if (response?.url) {
                    sessionStorage.setItem('sso_email', ssoEmail);

                    const activeInviteCode =
                        invite?.inviteCode ??
                        searchParams.get('invite') ??
                        parseStoredInviteValue(localStorage.getItem('inviteValue'))?.inviteCode;
                    if (activeInviteCode) {
                        sessionStorage.setItem(SSO_INVITE_CODE_SESSION_KEY, activeInviteCode);
                    }
                    
                    window.location.href = response.url;
                } else {
                    setLoading(false);
                }
            }
        }
        catch (error) {
            console.log('Sign up with SSO error', error);
            setLoading(false);
        }
    }, [ssoEmail, ssoValid, invite?.inviteCode, searchParams]);

    const handleSsoKeyDown = (event: any) => {
        if (event.key === 'Enter' && ssoValid) {
            event.preventDefault();
            SignupWithSSO();
        }
    };

    const inviteLanding = invite?.landing;
    const isInviteSignup = Boolean(inviteLanding);
    const signupTitle = isInviteSignup && inviteLanding
        ? getInviteSignupTitle(inviteLanding)
        : 'Create your account';
    const signupSubtitle = isInviteSignup && inviteLanding
        ? getInviteSignupSubtitle(inviteLanding)
        : 'Start your free 7-day trial. No credit card required.';
    const inviteCode = invite?.inviteCode ?? searchParams.get('invite') ?? undefined;

    return (
        <AuthPageContainer>
            {step === 1 ? (
            <AuthInnerContainer>
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        {isInviteSignup && inviteLanding ? (
                            <>
                                <AuthTitle>{getInviteSignupPageTitle()}</AuthTitle>
                                <AuthSubtitle>{getInviteSignupPageSubtitle()}</AuthSubtitle>
                            </>
                        ) : (
                            <>
                                <AuthTitle>{signupTitle}</AuthTitle>
                                <AuthSubtitle>{signupSubtitle}</AuthSubtitle>
                            </>
                        )}
                    </HeaderSection>
                    {isInviteSignup && inviteLanding ? (
                        <InviteSignupMethodStep
                            landing={inviteLanding}
                            inviteCode={inviteCode}
                            loading={loading}
                            onGoogleSignUp={onGoogleSignUpClick}
                            onEmailSignUp={() => setStep(2)}
                            onSsoSignUp={() => {
                                if (ssoValid && ssoEmail) {
                                    void SignupWithSSO();
                                    return;
                                }
                                setStep(3);
                            }}
                        />
                    ) : (
                        <AuthCard>
                            <AuthMethodButton
                                onClickHandler={onGoogleSignUpClick}
                                disabled={loading}
                                $type="blue"
                                tooltipMessage=''
                            >
                                <SvgFragment type='google' />
                                Register with Google
                            </AuthMethodButton>
                            <OrDivider>
                                <DividerLine />
                                <OrText>Or</OrText>
                                <DividerLine />
                            </OrDivider>
                            <AuthMethodButton
                                $type='light'
                                tooltipMessage=''
                                onClickHandler={() => setStep(2)}
                            >
                                Register with Email
                            </AuthMethodButton>
                            <AuthMethodButton
                                $type='light'
                                tooltipMessage=''
                                onClickHandler={() => setStep(3)}
                            >
                                Register with SSO
                            </AuthMethodButton>
                            <AuthSignUpLoginPrompt inviteCode={inviteCode} />
                            <AuthSignUpAgreementText />
                        </AuthCard>
                    )}
                </AuthColumn>
            </AuthInnerContainer>
            ) : step === 2 && emailSent ? (
            <AuthInnerContainer>
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        <AuthTitle>Check your inbox</AuthTitle>
                        <AuthSubtitle>
                            {isInviteSignup && inviteLanding
                                ? `We sent a verification link to ${values.email}. Once verified, you'll join ${inviteLanding.invite?.workspace?.name || 'the workspace'}.`
                                : `We sent a verification link to ${values.email}`}
                        </AuthSubtitle>
                    </HeaderSection>
                    <AuthVerificationCard>
                        <AuthVerificationCardInner>
                            <EmailClientButtons>
                                <EmailClientButton
                                    type="button"
                                    onClick={() => window.open('https://mail.google.com/mail/u/0/#inbox', '_blank')}
                                >
                                    <GmailIcon />
                                    <span>Open Gmail</span>
                                </EmailClientButton>
                                <EmailClientButton
                                    type="button"
                                    onClick={() => window.open('https://outlook.live.com/mail/0/inbox', '_blank')}
                                >
                                    <OpenOutlookIcon />
                                    <span>Open Outlook</span>
                                </EmailClientButton>
                            </EmailClientButtons>
                            <AuthResendLink
                                type="button"
                                disabled={loading}
                                onClick={resendVerificationEmail}
                            >
                                Resend verification link
                            </AuthResendLink>
                            <OrDivider>
                                <DividerLineMuted />
                                <OrText>Or</OrText>
                                <DividerLineMuted />
                            </OrDivider>
                            <AuthMethodButton
                                onClickHandler={onGoogleSignUpClick}
                                disabled={loading}
                                $type="blue"
                                tooltipMessage=''
                            >
                                <SvgFragment type='google' />
                                Register with Google
                            </AuthMethodButton>
                            <AuthSignUpLoginPrompt inviteCode={inviteCode} />
                        </AuthVerificationCardInner>
                    </AuthVerificationCard>
                </AuthColumn>
            </AuthInnerContainer>
            ) : step === 2 ? (
            <AuthInnerContainer>
                <form onKeyDown={handleKeyDown}>
                    <AuthColumn>
                        <FullLogo />
                        <HeaderSection>
                            <AuthTitle>Register with email address</AuthTitle>
                        </HeaderSection>
                        <AuthCard>
                            <AuthFormFields>
                                <AuthField>
                                    Email address
                                    <AuthInputWrap>
                                        <Input
                                            placeholder='Enter your email address'
                                            type='email'
                                            value={values.email || ''}
                                            onChangeHandler={valueNew => {
                                                updateValue('email', valueNew);
                                                updateValue('firstName', valueNew);
                                                updateValue('lastName', valueNew);
                                            }}
                                            onFocus={resetEmailCheckState}
                                            $error={!!emailCheckError}
                                            className={emailCheckError ? 'email-error' : undefined}
                                        />
                                    </AuthInputWrap>
                                    {!!emailCheckError && <EmailCheckError>{emailCheckError}</EmailCheckError>}
                                </AuthField>
                                <AuthField>
                                    Password
                                    <AuthPasswordFieldWrap>
                                        <AuthPasswordInputWrap>
                                            <Input
                                                placeholder='At least 8 characters long'
                                                type={showPassword ? 'text' : 'password'}
                                                value={values.password || ''}
                                                onChangeHandler={valueNew => updateValue('password', valueNew)}
                                                onFocus={() => void ensureEmailChecked()}
                                                $border={`1px solid ${styles.colors.black200}`}
                                            />
                                            <AuthPasswordToggle
                                                type="button"
                                                onClick={() => setShowPassword(previous => !previous)}
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            >
                                                <PasswordVisibilityIcon visible={showPassword} />
                                            </AuthPasswordToggle>
                                        </AuthPasswordInputWrap>
                                    </AuthPasswordFieldWrap>
                                </AuthField>
                            </AuthFormFields>
                            <AuthMethodButton
                                type='submit'
                                $type='light'
                                tooltipMessage=''
                                disabled={!valid || loading || emailCheckLoading || !!emailCheckError}
                                onlySpinner={loading}
                                spinnerSvg
                                onClickHandler={(event) => SignupWithEmail(event)}
                            >
                                Continue with Email
                            </AuthMethodButton>
                            <AuthSignUpLoginPrompt inviteCode={inviteCode} />
                            <AuthSignUpAgreementText />
                        </AuthCard>
                    </AuthColumn>
                </form>
            </AuthInnerContainer>
            ) : (
            <AuthInnerContainer>
                <form onKeyDown={handleSsoKeyDown}>
                    <AuthColumn>
                        <FullLogo />
                        <HeaderSection>
                            <AuthTitle>
                                {isInviteSignup && inviteLanding
                                    ? getInviteSignupPageTitle()
                                    : 'Register with SSO'}
                            </AuthTitle>
                            <AuthSubtitle>
                                {isInviteSignup && inviteLanding
                                    ? getInviteSignupEmailStepSubtitle(inviteLanding)
                                    : "Enter your work email to sign up with your company's Single Sign-On."}
                            </AuthSubtitle>
                        </HeaderSection>
                        <AuthCard>
                            <AuthFormFields>
                                <AuthField>
                                    Work email address
                                    <AuthInputWrap>
                                        <Input
                                            placeholder='you@company.com'
                                            type='email'
                                            value={ssoEmail}
                                            onChangeHandler={valueNew => updateSsoEmail(valueNew)}
                                            disabled={!!invite}
                                        />
                                    </AuthInputWrap>
                                </AuthField>
                            </AuthFormFields>
                            <AuthMethodButton
                                type='submit'
                                $type='light'
                                tooltipMessage=''
                                disabled={!ssoValid || loading}
                                onlySpinner={loading}
                                spinnerSvg
                                onClickHandler={(event) => SignupWithSSO(event)}
                            >
                                Continue with SSO
                            </AuthMethodButton>
                            <AuthSignUpLoginPrompt inviteCode={inviteCode} />
                            <AuthSignUpAgreementText />
                            <AuthCardInlineFooter>
                                <AuthLink onClick={() => setStep(1)}>
                                    {isInviteSignup ? 'Back to invite' : 'Back to sign up'}
                                </AuthLink>
                            </AuthCardInlineFooter>
                        </AuthCard>
                    </AuthColumn>
                </form>
            </AuthInnerContainer>
            )}
            <AuthLegalFooter />
      </AuthPageContainer>
    );
});

const EmailCheckError = styled.span`
    color: ${styles.colors.red600};
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
`;

const GmailIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M19.6 10.23c0-.68-.06-1.36-.18-2H10v3.79h5.48a4.68 4.68 0 01-2.03 3.07v2.55h3.28c1.92-1.77 3.03-4.38 3.03-7.41z" fill="#4285F4"/>
        <path d="M10 20c2.7 0 4.97-.89 6.63-2.41l-3.28-2.55c-.91.61-2.07.97-3.35.97-2.57 0-4.75-1.74-5.53-4.07H1.08v2.6A9.99 9.99 0 0010 20z" fill="#34A853"/>
        <path d="M4.47 11.94A5.99 5.99 0 014.13 10c0-.67.12-1.32.34-1.94V5.46H1.08A9.99 9.99 0 000 10c0 1.64.39 3.19 1.08 4.54l3.39-2.6z" fill="#FBBC05"/>
        <path d="M10 4.01c1.47 0 2.78.51 3.81 1.51l2.85-2.85C14.97 1.13 12.7.01 10 .01A9.99 9.99 0 001.08 5.46l3.39 2.6C5.25 5.75 7.43 4.01 10 4.01z" fill="#EA4335"/>
    </svg>
);

export default SignUpSection; 
